import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Card, CardText } from 'react-toolbox/lib/card';
import { RadioGroup, RadioButton } from 'react-toolbox/lib/radio';
import { LOCATION_ORIGIN } from '../../../../common/call-api';
import AuthCheck from '../../../../common/components/AuthenticationCheck';
import Redirect from '../../../../common/components/Redirect';
import {Button} from '../../../../common/components';
import HorizontalRule from '../HorizontalRule';
import {
  TitleBar,
  DocTitle,
} from '../../../../common/components/Layout/StandardPageTemplate/Context';
import CurrentUserActions, {
  getCurrentUser,
} from '../../../../redux-modules/current-user';
import { withLoading } from '../../../../redux-modules/loading-overlay';
import withQuery from '../../../../common/components/withQuery';
import PreferencesActions from '../../actions/customers/preferences';
import CustomerActions from '../../actions/customers';
import { getPreferences } from '../../selectors/customers/preferences';
import ActionHandler from '../ActionHandler';
import Switch from '../ThemedSwitch';
import SwitchList from '../SwitchList';
import styles from './PreferencesPage.css';
import { customSwitch } from '../SwitchList/SwitchList.css';

class PreferencesPage extends React.Component {
  static propTypes = {
    isLoading: PropTypes.bool,
    currentUser: PropTypes.shape({
      id: PropTypes.string,
    }),
    emailContact: PropTypes.bool,
    emailFormatType: PropTypes.string,
    phoneContact: PropTypes.bool,
    preferences: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        displayText: PropTypes.string,
        category: PropTypes.oneOf([
          'None',
          'CommunicationPreferences',
          'MemberPrivacy',
          'GetInvolved',
          'TopicsOfConcern',
          'MemberBenefitInterests',
        ]),
        isSelected: PropTypes.bool,
      })
    ),
    location: PropTypes.shape({
      query: PropTypes.shape({
        email: PropTypes.string,
      }),
    }),
    actions: PropTypes.shape({
      preferences: PropTypes.objectOf(PropTypes.func),
    }),
  };
  static promises = {
    includePromises: [
      { type: 'ENTITY/ENTITY_GET', meta: { namespace: 'preferences' } },
      { type: 'ENTITY/ENTITY_GET', meta: { namespace: 'customers' } },
      { type: 'ENTITY/ENTITY_PUT', meta: { namespace: 'preferences' } },
    ],
  };

  constructor(props) {
    super(props);
    this.state = {
      form: this.loadPreferencesFromProps(props),
      save: {
        shouldSave: false,
        data: [],
      },
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.isLoading) {
      return;
    }
    this.setState(prevState => {
      return {
        ...prevState,
        form: {
          ...prevState.form,
          ...this.loadPreferencesFromProps(nextProps),
        },
      };
    });
  }

  setFormValue = formProps => {
    this.setState(prevState => {
      return {
        ...prevState,
        form: {
          ...prevState.form,
          ...formProps,
        },
      };
    });
  };

  loadPreferencesFromProps = props => {
    const { emailContact, emailFormatType, phoneContact, preferences } = props;
    return {
      emailContact,
      emailFormatType,
      phoneContact,
      ...preferences.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.key]: curr.isSelected,
        };
      }, {}),
    };
  };

  handleChecked = key => value => {
    this.setFormValue({ [key]: value });
  };

  unknownUserAction = () => {
    const { location: { query }, actions } = this.props;
    return actions.customers.get(`?email=${query.email}`).then(results => {
      const { payload: { json, resp } } = results;
      if (resp.ok) {
        actions.currentUser.setId(json.key);
      }
      if (resp.status === 404) {
        this.setState({ isProspect: true });
      }
      return results;
    });
  };

  updateSave = ({ shouldSave, data = [] }) => {
    this.setState(prevState => ({
      ...prevState,
      save: {
        shouldSave,
        data,
      },
    }));
  };

  saveAction = data => {
    this.updateSave({ shouldSave: false });
    return this.props.actions.preferences.put(data);
  };

  handleSubmit = () => {
    const {
      emailContact,
      emailFormatType,
      phoneContact,
      ...preferences
    } = this.state.form;
    const data = {
      isEmailAllowed: emailContact,
      isPhoneAllowed: phoneContact,
      emailFormatType,
      items: this.props.preferences.map(i => {
        return {
          ...i,
          isSelected: preferences[i.key],
        };
      }),
    };
    this.updateSave({ shouldSave: true, data });
  };

  render() {
    const { preferences, currentUser, location: { query } } = this.props;
    const { email } = query || {};

    if (!email && !currentUser.id) {
      return (
        <Redirect
          external
          to={`${LOCATION_ORIGIN}/auth/login?message=Please log in to edit your preferences.&returnUrl=${LOCATION_ORIGIN}/account/preferences`}
        />
      );
    }
    const filteredPreferences = preferences.filter(
      i => i.category === 'CommunicationPreferences' && i.key
    );
    const emailOptedOut = !this.state.form.emailContact;
    const pageColor = 'purple';
    const pageTitle = 'Communication Preferences';
    return (
      <div>
        <TitleBar title={pageTitle} />
        <DocTitle title={pageTitle} />
        <h4 className={styles.header}>I am interested in</h4>
        {this.state.isProspect && (
          <Redirect
            external
            push
            to={`redirect/email-preferences?email=${email}`}
          />
        )}
        <Card className={styles.card}>
          <CardText>
            <ActionHandler
              condition={
                !!this.props.currentUser.id &&
                (!preferences || !preferences.length)
              }
              action={this.props.actions.preferences.get}
            />
            <ActionHandler
              condition={!!(query || {}).email && !this.props.isLoading}
              action={this.unknownUserAction}
            />
            <ActionHandler
              condition={this.state.save.shouldSave && !this.props.isLoading}
              action={this.saveAction}
              data={[this.state.save.data]}
            />
            <SwitchList
              data={this.state.form}
              items={filteredPreferences}
              sortKey="order"
              color={pageColor}
              onChange={this.handleChecked}
            >
              <h4 style={{ textAlign: 'center' }}>Loading...</h4>
            </SwitchList>
          </CardText>
        </Card>
        <h4 className={styles.header}>How may we contact you</h4>
        <Card className={styles.card}>
          <CardText>
            <Switch
              label="Email"
              onChange={this.handleChecked('emailContact')}
              color={pageColor}
              checked={this.state.form.emailContact}
              className={customSwitch}
            />
            <p
              style={{
                marginBottom: '2rem',
              }}
              className={styles.helpText}
            >
              Selecting off will opt you out of all future Marketing
              communications from NFIB
            </p>
            <label className={styles.emailFormatLabel}>
              <span
                style={{
                  color: emailOptedOut ? 'rgba(0, 0, 0, 0.26)' : '#000',
                }}
              >
                Email Format
              </span>
              <RadioGroup
                value={this.state.form.emailFormatType}
                disabled={emailOptedOut}
                onChange={this.handleChecked('emailFormatType')}
                className={styles.purple}
              >
                <RadioButton label="Plain Text" value="Text" theme={styles} />
                <RadioButton label="HTML" value="HTML" theme={styles} />
              </RadioGroup>
            </label>
            <Switch
              label="Phone"
              onChange={this.handleChecked('phoneContact')}
              color={pageColor}
              checked={this.state.form.phoneContact}
              className={customSwitch}
            />
          </CardText>
        </Card>
        <Button
          onClick={this.handleSubmit}
          className={styles.button}
          style={{ marginTop: 16, width: 'fit-content' }}
          label="Save"
        />
        <HorizontalRule color={pageColor} />
        <AuthCheck>
          {({ success }) => {
            const buttonLabel = success
              ? 'Back to Account Management'
              : 'Login';
            return (
              <Button
                to="/account"
                className={styles.button}
                label={buttonLabel}
                style={{ width: 'fit-content' }}
              />
            );
          }}
        </AuthCheck>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const currentUser = getCurrentUser(state);
  return {
    currentUser,
    ...(currentUser.settings || {}).contactPreferences,
    preferences: getPreferences(state),
  };
};

// return dispatch to be used in mergeProps where binding will take place
const mapDispatchToProps = dispatch => {
  return { dispatch };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return {
    ...ownProps,
    ...stateProps,
    actions: {
      preferences: bindActionCreators(
        PreferencesActions(stateProps.currentUser.id),
        dispatchProps.dispatch
      ),
      customers: bindActionCreators(CustomerActions, dispatchProps.dispatch),
      currentUser: bindActionCreators(
        CurrentUserActions,
        dispatchProps.dispatch
      ),
    },
  };
};

export default withQuery(
  withLoading(
    connect(mapStateToProps, mapDispatchToProps, mergeProps)(PreferencesPage)
  )
);



// WEBPACK FOOTER //
// ./src/App/account/components/PreferencesPage/index.js